import OrgSwitcher from '@/components/org-switcher';
import { Dialog, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  Cog6ToothIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { FeatureService, FeaturesResult } from '@/api';
import { useStore } from '@nanostores/react';
import {
  Bot,
  BrainCircuit,
  Building2,
  Captions,
  Computer,
  Database,
  FlaskConical,
  Folder,
  Home,
  Landmark,
  List,
  Mail,
  Sheet,
  Sparkles,
  Users,
  Workflow,
} from 'lucide-react';
import React, { Fragment, useEffect, useState } from 'react';
import TennisIcon from '../assets/tennis_icon.png';
import { $router } from '../stores/router';
import { LeftNavItem } from './components/LeftNavItem';

type Route = [
  string,
  RegExp,
  (...params: string[]) => object,
  (string | undefined)?,
];
function getPathsByName(name: string, routes: Route[]): (RegExp | undefined)[] {
  return routes
    .filter(route => route[3]?.includes(name))
    .map(route => route[1]); // Get the path from the fourth item in the array
}

interface LeftSidebarProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
const LeftSidebar: React.FC<LeftSidebarProps> = ({ open, setOpen }) => {
  const [isHovering, setIsHovering] = React.useState(false);
  const org = useStore($org);
  const [features, setFeatures] = useState<FeaturesResult[]>([]); // Define the state type
  const isCollapsed = useStore($sideBarCollapsed);
  const [showPropensitySubMenu, setShowPropensitySubMenu] = useState(false);

  useEffect(() => {
    const fetchFeatures = async () => {
      const orgId = org?.id; // Get the organization ID
      if (orgId) {
        const fetchedFeatures = await FeatureService.getFeatures(orgId); // Call the service and set to constant
        setFeatures(fetchedFeatures);
      }
    };

    fetchFeatures();
  }, [org]);

  const isKnowledgeBaseEnabled = features.some(
    feature => feature.name === 'Knowledge Base' && feature.enabled,
  );
  const isChatbotAgentEnabled = features.some(
    feature => feature.name === 'Chatbot Agent' && feature.enabled,
  );
  const isPortfolioEnabled = features.some(
    feature => feature.name === 'Portfolios' && feature.enabled,
  );
  const isTranscribeEnabled = features.some(
    feature => feature.name === 'Transcribe' && feature.enabled,
  );

  const isIndicatorScoresEnabled = features.some(
    feature => feature.name === 'Indicator Scores' && feature.enabled,
  );
  const isScoringLogicEnabled = features.some(
    feature => feature.name === 'Scoring Logic' && feature.enabled,
  );

  const isComplaintsEnabled = features.some(
    feature => feature.name === 'Complaints' && feature.enabled,
  );

  const isExperimentsEnabled = features.some(
    feature => feature.name === 'Experiments' && feature.enabled,
  );

  const isWorkspacesEnabled = features.some(
    feature => feature.name === 'Workspaces' && feature.enabled,
  );

  const isSmartAnalyzeEnabled = features.some(
    feature => feature.name === 'Smart Analyze' && feature.enabled,
  );

  const isSmartInboxEnabled = features.some(
    feature => feature.name === 'Smart Inbox' && feature.enabled,
  );

  const isARTasksEnabled = features.some(
    feature => feature.name === 'AR Tasks' && feature.enabled,
  );

  const page = useStore($router);

  const sidebarNavList = (
    <ul role="list" className="-mx-2 space-y-2">
      <LeftNavItem
        text="Home"
        icon={<Home className="h-5 w-5" />}
        onClick={() => {
          openPage($router, 'home');
          setOpen(false);
        }}
        isCollapsed={isCollapsed}
        routes={getPathsByName('home', $router.routes)}
      />
      {!!org && (
        <LeftNavItem
          text="Accounts"
          icon={<Users className="h-5 w-5" />}
          onClick={() => {
            openPage($router, 'portfolios', {
              organizationId: org!.id,
            });
            setOpen(false);
          }}
          isCollapsed={isCollapsed}
          routes={getPathsByName('portfolio', $router.routes)}
        />
      )}
      {/* <LeftNavItem
        text="Organizations"
        icon={<Network className="h-5 w-5" />}
        onClick={() => {
          openPage($router, 'organizations');
          setOpen(false);
        }}
        isCollapsed={isCollapsed}
      /> */}
      {!!org && isWorkspacesEnabled && (
        <LeftNavItem
          text="Workspaces"
          icon={<Folder className="h-5 w-5" />}
          onClick={() => {
            openPage($router, 'workspaces', {
              organizationId: org!.id,
            });
            setOpen(false);
          }}
          isCollapsed={isCollapsed}
          routes={getPathsByName('workspace', $router.routes)}
        />
      )}

      {!!org && (
        <LeftNavItem
          text="Workflows"
          icon={<Workflow className="h-5 w-5" />}
          onClick={() => {
            openPage($router, 'workflows_o', {
              organizationId: org!.id,
            });
            setOpen(false);
          }}
          isCollapsed={isCollapsed}
          routes={getPathsByName('workflows_o', $router.routes)}
        />
      )}
      {/* TODO: Nest these 2 under a "Propensity to pay" nav item in the future*/}
      {!!org && isIndicatorScoresEnabled && (
        <LeftNavItem
          text="Indicator Scores"
          icon={<Database className="h-5 w-5" />}
          onClick={() => {
            openPage($router, 'indicatorscores', {
              organizationId: org!.id,
            });
            setOpen(false);
          }}
          isCollapsed={isCollapsed}
        />
      )}
      {!!org && isScoringLogicEnabled && (
        <LeftNavItem
          text="Scoring Logic"
          icon={<Computer className="h-5 w-5" />}
          onClick={() => {
            openPage($router, 'score_logics', {
              organizationId: org!.id,
            });
            setOpen(false);
          }}
          isCollapsed={isCollapsed}
        />
      )}
      {!!org && isComplaintsEnabled && (
        <LeftNavItem
          text="Complaints"
          icon={<Sheet className="h-5 w-5" />}
          onClick={() => {
            openPage($router, 'complaints', {
              organizationId: org!.id,
            });
            setOpen(false);
          }}
          isCollapsed={isCollapsed}
        />
      )}
      {!!org && isExperimentsEnabled && (
        <LeftNavItem
          text="Experiments"
          icon={<FlaskConical className="h-5 w-5" />}
          onClick={() => {
            openPage($router, 'experiments', {
              organizationId: org!.id,
            });
            setOpen(false);
          }}
          isCollapsed={isCollapsed}
        />
      )}
      {/* {!!workspace && (
        <LeftNavItem
          text="Projects"
          icon={<FileBox className="h-5 w-5" />}
          onClick={() => {
            openPage($router, 'projects', {
              organizationId: org!.id,
              workspaceId: workspace!.workspace.id,
            });
            setOpen(false);
          }}
          isCollapsed={isCollapsed}
        />
      )} */}
      {/* Conditionally render Knowledge Base and Chatbot Agent */}
      {!!org && isChatbotAgentEnabled && (
        <LeftNavItem
          text="Chatbot Agent"
          icon={<Bot className="h-5 w-5" />}
          onClick={() => {
            openPage($router, 'chatbot');
            setOpen(false);
          }}
          isCollapsed={isCollapsed}
          routes={getPathsByName('chatbot', $router.routes)}
        />
      )}
      {!!org && isTranscribeEnabled && (
        <LeftNavItem
          text="Transcribe"
          icon={<Captions className="h-5 w-5" />}
          onClick={() => {
            openPage($router, 'transcriptions', {
              organizationId: org!.id,
            });
            setOpen(false);
          }}
          isCollapsed={isCollapsed}
        />
      )}
      {!!org && isKnowledgeBaseEnabled && (
        <LeftNavItem
          text="Knowledge Base"
          icon={<BrainCircuit className="h-5 w-5" />}
          onClick={() => {
            openPage($router, 'knowledgebases', {
              organizationId: org!.id,
            });
            setOpen(false);
          }}
          isCollapsed={isCollapsed}
          routes={getPathsByName('knowlegebase', $router.routes)}
        />
      )}
      {!!org && isSmartAnalyzeEnabled && (
        <LeftNavItem
          text="Smart Analyze"
          icon={<Sparkles className="h-5 w-5" />}
          onClick={() => {
            openPage($router, 'smartanalyze');
            setOpen(false);
          }}
          isCollapsed={isCollapsed}
          routes={getPathsByName('smartanalyze', $router.routes)}
        />
      )}

      {!!org && isSmartInboxEnabled && (
        <LeftNavItem
          text="Smart Inbox"
          icon={<Mail className="h-5 w-5" />}
          onClick={() => {
            openPage($router, 'inbox', {
              organizationId: org!.id,
            });
            setOpen(false);
          }}
          isCollapsed={isCollapsed}
          routes={getPathsByName('inbox', $router.routes)}
          // ^ This optional "routes" prop is how you highlight the item if the current route matches.
        />
      )}
      {!!org && isARTasksEnabled && (
        <LeftNavItem
          text="AR Tasks"
          icon={<List className="h-5 w-5" />}
          onClick={() => {
            openPage($router, 'tasks', {
              organizationId: org!.id,
            });
            setOpen(false);
          }}
          isCollapsed={isCollapsed}
          routes={getPathsByName('tasks', $router.routes)}
          // ^ This optional "routes" prop is how you highlight the item if the current route matches.
        />
      )}
    </ul>
  );
  const [showText, setShowText] = useState(!isCollapsed);
  useEffect(() => {
    if (!isCollapsed) {
      const timer = setTimeout(() => setShowText(true), 300); // Adjust delay as needed
      return () => clearTimeout(timer);
    } else {
      setShowText(false);
    }
  }, [isCollapsed]);

  return (
    <>
      {/* Mobile sidebar with transition */}
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>

                {/* Sidebar component, swap this element with another sidebar if you like */}
                <div className="flex grow flex-col gap-y-5 overflow-y-auto border bg-background">
                  <div className="flex h-16 shrink-0 items-center justify-center border-b px-6">
                    <img src={TennisIcon} className="mr-2 w-[34px] " />

                    <span className="text-lg text-foreground">
                      Rally | Tennis Finance
                    </span>
                  </div>
                  <nav className="flex flex-1 flex-col px-6 pb-4">
                    <ul role="list" className="flex flex-1 flex-col gap-y-7">
                      <li>{sidebarNavList}</li>

                      <ul className="mt-auto">
                        <LeftNavItem
                          text="Settings"
                          icon={
                            <Cog6ToothIcon
                              className="h-6 w-6 shrink-0"
                              aria-hidden="true"
                            />
                          }
                          onClick={() => {
                            openPage($router, 'settings');
                            setOpen(false);
                          }}
                          isCollapsed={false} // Always show text in mobile view
                        />
                      </ul>
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div
        className={`transition-width hidden duration-300 lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col ${
          isCollapsed ? 'lg:w-20' : 'lg:w-72'
        }`}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {/* Sidebar component, swap this element with another sidebar if you like */}
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border border-t-0 bg-background">
          <div className="flex h-16 shrink-0 items-center justify-between border-b px-6">
            <div className="flex items-center">
              {!isCollapsed && (
                <>
                  <img src={TennisIcon} className="mr-1 w-[34px]" />
                  {showText && (
                    <span className="text-lg text-foreground opacity-100 transition-opacity duration-300">
                      Rally | Tennis Finance
                    </span>
                  )}
                </>
              )}
            </div>
            <button
              type="button"
              className="-m-2.5 p-2.5 text-muted-foreground"
              onClick={() => $sideBarCollapsed.set(!isCollapsed)}
            >
              {isCollapsed ? (
                isHovering ? (
                  <ChevronDoubleRightIcon
                    className="h-6 w-6 text-gray-600"
                    aria-hidden="true"
                  />
                ) : (
                  <Bars3Icon
                    className="h-6 w-6 text-gray-600"
                    aria-hidden="true"
                  />
                )
              ) : (
                isHovering && (
                  <ChevronDoubleLeftIcon
                    className="h-6 w-6 text-gray-600"
                    aria-hidden="true"
                  />
                )
              )}
            </button>
          </div>
          <nav className="flex flex-1 flex-col px-6 pb-4">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              {!isCollapsed && (
                <li>
                  {' '}
                  <OrgSwitcher className="-mb-6 w-full" />
                </li>
              )}

              <li>{sidebarNavList}</li>

              <ul className="mt-auto">
                <LeftNavItem
                  text="Settings"
                  icon={
                    <Cog6ToothIcon
                      className="h-6 w-6 shrink-0"
                      aria-hidden="true"
                    />
                  }
                  onClick={() => openPage($router, 'settings')}
                  isCollapsed={isCollapsed}
                  routes={getPathsByName('settings', $router.routes)}
                />
              </ul>
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
};

export default LeftSidebar;
